import { Fragment, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useNavigate, useSearchParams } from 'react-router-dom';
import { Menu, MenuItem, MenuButton, MenuItems, Dialog, Transition, TransitionChild, DialogPanel, DialogTitle } from '@headlessui/react';
import axios from '../../api/axiosInstance';
import CircularProgress from '@mui/material/CircularProgress';
import { BellIcon, ChevronDownIcon, UserCircleIcon, ChatBubbleOvalLeftIcon, PlusCircleIcon, Bars3Icon, ExclamationTriangleIcon, CalendarIcon } from '@heroicons/react/24/outline';
import Loader from './components/Loader';
import ErrorBox from './components/ErrorBox';
import MetricsBoard from './analytics/MetricsBoard';
import UpcomingEvents from './components/UpcomingEvents';
import ReviewsSummary from './components/ReviewsSummary';
import ActivityLog from './components/ActivityLog';
import NotificationsList from './NotificationsList';
import BusinessStatus from '../business-onboarding/BusinessStatus';

function getBusinessId(business) {
  if (!business) return '';
  const raw = business.id ?? business.business_id ?? business.businessId ?? '';
  return raw == null ? '' : String(raw);
}

export default function BusinessAdminPanel() {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const currentBusinessId = (search.get('business_id') ?? '').trim();
  const hasSelectedBusiness = currentBusinessId !== '';

  const [businesses, setBusinesses] = useState([]);
  const [bizLoading, setBizLoading] = useState(true);
  const [bizErr, setBizErr] = useState(null);

  const [confirmFeature, setConfirmFeature] = useState(null);
  const [notifCount, setNotifCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    setBizLoading(true);

    const load = async () => {
      try {
        // Prefer V2 businesses (includes onboarding drafts)
        const { data } = await axios.get('/businesses/my');
        const list =
          (Array.isArray(data?.data?.businesses) && data.data.businesses) ||
          (Array.isArray(data?.businesses) && data.businesses) ||
          (Array.isArray(data?.data) && data.data) ||
          [];
        if (mounted) setBusinesses(list);
      } catch (err) {
        // Back-compat with legacy endpoint
        try {
          const { data } = await axios.get('/business/my');
          const list = (Array.isArray(data) && data) || (Array.isArray(data?.data) && data.data) || [];
          if (mounted) setBusinesses(list);
        } catch (e2) {
          if (mounted) setBizErr(err);
        }
      } finally {
        if (mounted) setBizLoading(false);
      }
    };

    load();

    return () => (mounted = false);
  }, []);

  useEffect(() => {
    if (!hasSelectedBusiness) return;

    const controller = new AbortController();
    axios
      .get('/dashboard/notifications', {
        params: { business_id: currentBusinessId },
        signal: controller.signal,
      })
      .then(({ data }) => setNotifCount(data?.length ?? 0))
      .catch(() => setNotifCount(0));

    return () => controller.abort();
  }, [currentBusinessId, hasSelectedBusiness]);

  // Auto-select most recent business if none chosen
  useEffect(() => {
    if (hasSelectedBusiness || !businesses?.length) return;
    setSearch((prev) => {
      const next = new URLSearchParams(prev);
      const firstId = getBusinessId(businesses[0]);
      if (firstId) next.set('business_id', firstId);
      return next;
    });
  }, [businesses, hasSelectedBusiness, setSearch]);

  const changeBusiness = useCallback(
    (id) => {
      setSearch((prev) => {
        const next = new URLSearchParams(prev);
        next.set('business_id', id);
        return next;
      });
    },
    [setSearch],
  );

  const handleSelectBusiness = useCallback(
    (business) => {
      const id = getBusinessId(business);
      if (!id) return;

      // Draft businesses must be completed via onboarding.
      if (business.onboarding_completed === false) {
        navigate(`/business/onboarding/${encodeURIComponent(id)}`);
        return;
      }

      changeBusiness(id);
    },
    [changeBusiness, navigate],
  );

  const currentBusiness = useMemo(() => {
    if (!hasSelectedBusiness) return null;
    return businesses.find((b) => getBusinessId(b) === currentBusinessId) ?? null;
  }, [businesses, currentBusinessId, hasSelectedBusiness]);

  // Feature flags derived from current business object (assumes backend supplies these booleans or analogous fields)
  const reservationsEnabled = !!currentBusiness?.accepts_reservations;
  const productsEnabled = !!currentBusiness?.sells_products;

  const maybeRedirectToStripeConnect = useCallback(async () => {
    if (!hasSelectedBusiness) return false;
    try {
      const caps = await axios.get(`/businesses/${encodeURIComponent(currentBusinessId)}/capabilities`);
      const features = caps?.data?.data?.features;
      const canAcceptPayments = !!caps?.data?.data?.canAcceptPayments;
      const locationVerificationRequired = !!caps?.data?.data?.locationVerificationRequired;
      if ((features?.reservations || features?.products) && !canAcceptPayments) {
        if (locationVerificationRequired) {
          navigate(`/business/${encodeURIComponent(currentBusinessId)}/onboarding/location-letter-verify`);
          return true;
        }
        navigate(`/business/${encodeURIComponent(currentBusinessId)}/onboarding/stripe-refresh`);
        return true;
      }
    } catch (_) {
      return false;
    }
    return false;
  }, [currentBusinessId, hasSelectedBusiness, navigate]);

  const navigateWithStripeGuard = useCallback(
    async (basePath) => {
      if (!hasSelectedBusiness) return;
      const redirected = await maybeRedirectToStripeConnect();
      if (redirected) return;
      navigate(`${basePath}?business_id=${encodeURIComponent(currentBusinessId)}`);
    },
    [currentBusinessId, hasSelectedBusiness, maybeRedirectToStripeConnect, navigate],
  );

  // Enable feature handler
  const enableFeature = async (featureKey) => {
    if (!hasSelectedBusiness) return;

    try {
      // optimistic local update
      setBusinesses((prev) =>
        prev.map((b) =>
          getBusinessId(b) === currentBusinessId
            ? {
                ...b,
                ...(featureKey === 'reservations' && { accepts_reservations: true }),
                ...(featureKey === 'products' && { sells_products: true }),
              }
            : b,
        ),
      );

      const patch = {
        ...(featureKey === 'reservations' ? { acceptsReservations: true } : {}),
        ...(featureKey === 'products' ? { sellsProducts: true } : {}),
      };

      const { data } = await axios.patch(`/businesses/${encodeURIComponent(currentBusinessId)}`, patch);
      const updated = data?.data?.business;
      if (updated) {
        setBusinesses((prev) => prev.map((b) => (getBusinessId(b) === currentBusinessId ? { ...b, ...updated } : b)));
      }

      // If a payments-related feature was enabled, enforce Stripe onboarding.
      // (Stripe is required only when reservations or products are enabled.)
      const caps = await axios.get(`/businesses/${encodeURIComponent(currentBusinessId)}/capabilities`);
      const requested = caps?.data?.data?.features;
      const canAcceptPayments = !!caps?.data?.data?.canAcceptPayments;
      const locationVerificationRequired = !!caps?.data?.data?.locationVerificationRequired;
      if ((requested?.reservations || requested?.products) && !canAcceptPayments) {
        if (locationVerificationRequired) {
          navigate(`/business/${encodeURIComponent(currentBusinessId)}/onboarding/location-letter-verify`);
          return;
        }
        navigate(`/business/${encodeURIComponent(currentBusinessId)}/onboarding/stripe-refresh`);
      }
    } catch (e) {
      alert('Failed to enable feature.');
      // revert optimistic update by refetching single business
      axios.get('/businesses/my').then(({ data }) => setBusinesses(data?.data?.businesses ?? [])).catch(() => {});
    }
  };

  if (bizLoading) {
    return (
      <div className="flex h-screen flex-col items-center justify-center space-y-4">
        <CircularProgress size={40} sx={{ color: '#d1d5db' }}/>
      </div>
    );
  }

  if (bizErr) return <ErrorBox message="Failed to load businesses." />;

  return (
    <div className="tailwind min-h-screen flex flex-col bg-gray-50">
      <header className="sticky top-0 z-40 bg-white shadow">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Menu as="div" className="relative inline-block text-left">
              <MenuButton className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium shadow-sm focus:outline-none">
                <span className="max-w-[180px] truncate sm:max-w-[260px]">
                  {currentBusiness?.business_name ?? 'Select Business'}
                </span>
                {currentBusiness && currentBusiness?.onboarding_completed === false && (
                  <span className="ml-1 rounded-full bg-yellow-50 px-2 py-0.5 text-[11px] font-semibold text-yellow-700 ring-1 ring-yellow-200">
                    Draft
                  </span>
                )}
                <ChevronDownIcon className="h-4 w-4" />
              </MenuButton>

              <DropdownTransition>
                <MenuItems className="absolute left-0 mt-2 w-56 origin-top-left divide-y divide-gray-200 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {businesses.map((b) => (
                    <MenuItem
                      key={getBusinessId(b) || b.business_name || JSON.stringify(b)}
                      as="button"
                      onClick={() => handleSelectBusiness(b)}
                      className="flex w-full px-4 py-2 text-sm data-[focus]:bg-gray-100"
                    >
                      <div className="flex w-full items-center gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="truncate font-medium text-gray-900">{b.business_name || 'Untitled Business'}</div>
                          {'onboarding_completed' in b && (
                            <div className="truncate text-[11px] text-gray-500">
                              {b.onboarding_completed ? 'Onboarding complete' : 'Onboarding draft'}
                            </div>
                          )}
                        </div>
                        {'onboarding_completed' in b && !b.onboarding_completed && (
                          <span className="shrink-0 rounded-full bg-yellow-50 px-2 py-0.5 text-[11px] font-semibold text-yellow-700 ring-1 ring-yellow-200">
                            Draft
                          </span>
                        )}
                        {'is_verified' in b && b.is_verified && (
                          <span className="shrink-0 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700 ring-1 ring-green-200">
                            Verified
                          </span>
                        )}
                      </div>
                    </MenuItem>
                  ))}

                  <div className="py-1">
                    <MenuItem
                      as="button"
                      onClick={() => navigate('/business/onboarding/new')}
                      className="flex w-full px-4 py-2 text-sm data-[focus]:bg-gray-100"
                    >
                      Add New Business
                    </MenuItem>
                    {hasSelectedBusiness && (
                      <MenuItem
                        as="button"
                        onClick={() =>
                          navigate(
                            `/business/onboarding/${encodeURIComponent(currentBusinessId)}`,
                          )
                        }
                        className="flex w-full px-4 py-2 text-sm data-[focus]:bg-gray-100"
                      >
                        Edit Current Business
                      </MenuItem>
                    )}
                    {hasSelectedBusiness && (
                      <MenuItem
                        as="button"
                        onClick={() => {
                          const name = businesses.find(b => getBusinessId(b) === currentBusinessId)?.business_name;
                          if (window.confirm(`Are you sure you want to delete "${name || 'this business'}"?`)) {
                            axios
                              .delete(`/businesses/${encodeURIComponent(currentBusinessId)}`)
                              .catch(async (err) => {
                                if (err?.response?.status === 404) {
                                  return axios.delete(`/business/${encodeURIComponent(currentBusinessId)}`);
                                }
                                throw err;
                              })
                              .then(() => {
                                setBusinesses((prev) =>
                                  prev.filter((b) => getBusinessId(b) !== currentBusinessId)
                                );
                                setSearch((p) => {
                                  const next = new URLSearchParams(p);
                                  next.delete('business_id');
                                  return next;
                                });
                                alert('Business deleted');
                              })
                              .catch(() => {
                                alert('Failed to delete business');
                              });
                          }
                        }}
                        className="flex w-full px-4 py-2 text-sm text-red-600 data-[focus]:bg-gray-100"
                      >
                        Delete Business
                      </MenuItem>
                    )}
                  </div>
                </MenuItems>
              </DropdownTransition>
            </Menu>

            <div className="flex items-center gap-1">
              {hasSelectedBusiness && (
                <Menu as="div" className="relative">
                  <MenuButton className="relative p-2 text-gray-500 focus:outline-none">
                    <BellIcon className="h-6 w-6" />
                    {notifCount > 0 && (
                      <span className="absolute -right-1 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[11px] font-semibold text-white">
                        {notifCount}
                      </span>
                    )}
                  </MenuButton>

                  <DropdownTransition>
                    <MenuItems className="absolute right-0 mt-2 w-64 rounded-md bg-white p-4 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <NotificationsList
                        businessId={currentBusinessId}
                        onNavigate={(id) =>
                          navigate(`/notifications?business_id=${id}`)
                        }
                      />
                    </MenuItems>
                  </DropdownTransition>
                </Menu>
              )}

              {hasSelectedBusiness && (
                <Menu as="div" className="relative">
                  <MenuButton className="relative p-2 text-gray-500 focus:outline-none">
                    <span className="sr-only">Open actions menu</span>
                    <Bars3Icon className="h-6 w-6" />
                  </MenuButton>

                  <DropdownTransition>
                    <MenuItems className="absolute right-0 mt-2 w-64 rounded-md bg-white p-2 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      {/* Reservations Section */}
                      <div>
                        <p className="px-3 py-2 text-base font-semibold text-black">Reservations</p>
                        {reservationsEnabled ? (
                          <>
                            <MenuItem
                              as="button"
                              onClick={() => navigateWithStripeGuard('/business-admin/reservations/config')}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left data-[focus]:bg-gray-100"
                            >
                              Configure Reservations
                            </MenuItem>
                            <MenuItem
                              as="button"
                              onClick={() => navigateWithStripeGuard('/business-admin/employees')}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left data-[focus]:bg-gray-100"
                            >
                              Configure Employees
                            </MenuItem>
                            <MenuItem
                              as="button"
                              onClick={() => navigateWithStripeGuard('/business-admin/schedule')}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left data-[focus]:bg-gray-100"
                            >
                              Schedule
                            </MenuItem>
                          </>
                        ) : (
                          <MenuItem
                            as="button"
                            onClick={() => setConfirmFeature('reservations')}
                            className="mx-2 mb-2 block w-[calc(100%-1rem)] cursor-pointer rounded-md border border-dashed border-mainColor bg-indigo-50 px-3 py-2 text-xs text-mainColor text-left"
                          >
                            Enable Reservations
                          </MenuItem>
                        )}
                      </div>
                      {/* Products Section */}
                      <div className="pt-2">
                        <p className="px-3 py-2 text-base font-semibold text-black">Products</p>
                        {productsEnabled ? (
                          <>
                            <MenuItem
                              as="button"
                              onClick={() => navigateWithStripeGuard('/business-admin/store/products')}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left data-[focus]:bg-gray-100"
                            >
                              Configure Products
                            </MenuItem>
                            <MenuItem
                              as="button"
                              onClick={() => navigateWithStripeGuard('/business-admin/discounts')}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left data-[focus]:bg-gray-100"
                            >
                              Discounts
                            </MenuItem>
                            <MenuItem
                              as="button"
                              onClick={() => navigateWithStripeGuard('/business-admin/store/settings')}
                              className="flex w-full items-center gap-2 px-3 py-2 text-sm text-left data-[focus]:bg-gray-100"
                            >
                              Store Settings
                            </MenuItem>
                          </>
                        ) : (
                          <MenuItem
                            as="button"
                            onClick={() => setConfirmFeature('products')}
                            className="mx-2 mb-2 block w-[calc(100%-1rem)] cursor-pointer rounded-md border border-dashed border-mainColor bg-indigo-50 px-3 py-2 text-xs text-mainColor text-left"
                          >
                            Enable Products
                          </MenuItem>
                        )}
                      </div>
                    </MenuItems>
                  </DropdownTransition>
                </Menu>
              )}

              <Menu as="div" className="relative">
                <MenuButton className="flex items-center rounded-full text-gray-500 p-2 focus:outline-none">
                  <UserCircleIcon className="h-6 w-6" />
                </MenuButton>

                <DropdownTransition>
                  <MenuItems className="absolute right-0 mt-2 w-40 origin-top-right divide-y divide-gray-200 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <MenuItem
                      as={NavLink}
                      to="/"
                      className="block px-4 py-2 text-sm data-[focus]:bg-gray-100"
                    >
                      My User Account
                    </MenuItem>
                    <MenuItem
                      as="button"
                      onClick={() => {
                        axios.post('/auth/logout').finally(() => {
                          window.location.href = '/login';
                        });
                      }}
                      className="block w-full px-4 py-2 text-left text-sm text-red-600 data-[focus]:bg-gray-100"
                    >
                      Log out
                    </MenuItem>
                  </MenuItems>
                </DropdownTransition>
              </Menu>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1">
        {!hasSelectedBusiness ? (
          <EmptyState />
        ) : (
          <div className="mx-auto max-w-7xl space-y-8 p-6">
            {/* Business Status - Shows verification status and capabilities */}
            <BusinessStatus businessId={currentBusinessId} />

            {/* Quick actions cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <button
                onClick={() => navigate(`/business-admin/create-post?business_id=${encodeURIComponent(currentBusinessId)}`)}
                className="group rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <div className="flex items-center gap-4">
                  <span className="rounded-lg bg-indigo-50 p-2 text-mainColor ring-1 ring-indigo-100">
                    <PlusCircleIcon className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Add Post</p>
                    <p className="mt-1 text-sm text-gray-500">Create a new post to engage your audience.</p>
                  </div>
                </div>
              </button>

              {/* Add Event button */}
              <button
                onClick={() => navigateWithStripeGuard('/business-admin/schedule/calendar')}
                className="group rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <div className="flex items-center gap-4">
                  <span className="rounded-lg bg-indigo-50 p-2 text-mainColor ring-1 ring-indigo-100">
                    <CalendarIcon className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Add Event</p>
                    <p className="mt-1 text-sm text-gray-500">Create a new event for your business.</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => navigate(`/business-admin/inbox?business_id=${encodeURIComponent(currentBusinessId)}`)}
                className="group rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <div className="flex items-center gap-4">
                  <span className="rounded-lg bg-indigo-50 p-2 text-mainColor ring-1 ring-indigo-100">
                    <ChatBubbleOvalLeftIcon className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Inbox</p>
                    <p className="mt-1 text-sm text-gray-500">View and respond to messages.</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() =>
                  navigate(
                    `/business/${encodeURIComponent(currentBusiness?.business_name ?? '')}?business_id=${currentBusinessId}`,
                  )
                }
                className="group rounded-xl border border-gray-200 bg-white p-5 text-left shadow-sm transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <div className="flex items-center gap-4">
                  <span className="rounded-lg bg-indigo-50 p-2 text-mainColor ring-1 ring-indigo-100">
                    <UserCircleIcon className="h-6 w-6" />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Profile</p>
                    <p className="mt-1 text-sm text-gray-500">View your business profile.</p>
                  </div>
                </div>
              </button>
              {/* Enable feature cards moved into menu (removed from main grid) */}
            </div>

            <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-900">Manage</h2>
              <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <button
                  onClick={() => navigate(`/business-admin/inbox?business_id=${encodeURIComponent(currentBusinessId)}`)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-50"
                >
                  Inbox
                </button>
                <button
                  onClick={() => navigate(`/business-admin/analytics?business_id=${encodeURIComponent(currentBusinessId)}`)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-50"
                >
                  Analytics
                </button>
                <button
                  onClick={() => navigateWithStripeGuard('/business-admin/reservations/config')}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-50"
                >
                  Reservations
                </button>
                <button
                  onClick={() => navigateWithStripeGuard('/business-admin/employees')}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-50"
                >
                  Employees
                </button>
                <button
                  onClick={() => navigateWithStripeGuard('/business-admin/schedule/calendar')}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-50"
                >
                  Schedule
                </button>
                <button
                  onClick={() => navigateWithStripeGuard('/business-admin/store/products')}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-50"
                >
                  Products
                </button>
                <button
                  onClick={() => navigateWithStripeGuard('/business-admin/discounts')}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-50"
                >
                  Discounts
                </button>
                <button
                  onClick={() => navigateWithStripeGuard('/business-admin/store/settings')}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-left text-sm font-medium text-gray-900 hover:bg-gray-50"
                >
                  Store Settings
                </button>
              </div>
            </section>
            <MetricsBoard businessId={currentBusinessId} />
            <UpcomingEvents businessId={currentBusinessId} />
            <ReviewsSummary businessId={currentBusinessId} />
            <ActivityLog businessId={currentBusinessId} />
          </div>
        )}

        <Suspense fallback={<Loader fullScreen />}>
          <Outlet />
        </Suspense>
      </main>

      <Transition show={!!confirmFeature} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setConfirmFeature(null)}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" style={{ backdropFilter: 'blur(12px)' }}/>
          </TransitionChild>

          <div className="fixed inset-0 flex items-center justify-center p-4">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <DialogPanel className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
                <DialogTitle className="text-lg font-medium text-gray-900">
                  {confirmFeature === 'reservations' ? 'Enable Reservations?' : confirmFeature === 'products' ? 'Enable Products?' : ''}
                </DialogTitle>
                <p className="mt-2 text-sm text-gray-600">
                  Are you sure you want to enable this feature?
                </p>

                <div className="tailwind mt-4 flex justify-end gap-2">
                  <button
                    onClick={() => setConfirmFeature(null)}
                    className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      enableFeature(confirmFeature);
                      setConfirmFeature(null);
                    }}
                    className="rounded-md bg-mainColor px-3 py-1.5 text-sm font-medium text-white"
                  >
                    Enable
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

function DropdownTransition({ children }) {
  return (
    <Transition
      as={Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      {children}
    </Transition>
  );
}

function EmptyState() {
  return (
    <div className="tailwind flex flex-col items-center justify-center space-y-5 text-gray-500 mt-24">
      <p className="text-base">Select a business to begin</p>
      <button
        onClick={() => (window.location.href = '/business/onboarding/new')}
        className="rounded-md bg-mainColor px-4 py-2 text-white"
      >
        + Add New Business
      </button>
    </div>
  );
}
